// Main.tsx
import { useAppDispatch, useAppSelector } from '_hooks';
import { HelperMethods } from '_utils';
import React, { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import {
  AccountLayout,
  AuthLayout,
  ContainerLayout,
  PublicRoute,
  RoleGuard,
} from '_layouts';

// Pages
import {
  ContactUs,
  Hockey,
  Lessons,
  MyBookedParties,
  MyBookedSkatingPasses,
  Parties,
  Passes,
  Privacy,
  Tickets,
  Waivers,
} from '_pages';

// Auth Pages
import ForgotPassword from '_pages/Auth/ForgotPassword';
import SignIn from '_pages/Auth/SignIn';
import SignUp from '_pages/Auth/SignUp';

// Admin Pages
import {
  AdminHockeyLessons,
  ArchivedWaivers,
  BookedHockeyLessons,
  BookedParties,
  BookedSkatingLessons,
  BookedSkatingPasses,
  BookedSkatingSessions,
  ContactUsMessages,
  CouponManagement,
  CustomerWaivers,
  Emails,
  MyBookedHockeyLessons,
  MyBookedSkatingLessons,
  MyBookedSkatingSessions,
  MySubmittedWaivers,
  PartiesSettings,
  Profile,
  SeasonPriceList,
  SeasonSettings,
  SkatingLessons,
  SkatingPasses,
  SkatingSessions,
  TicketAuditLog,
  OrderSources,
  UserAccountDetail,
  UserAccounts,
  Waiver,
} from '_pages';

const Main: React.FC = () => {
  const dispatch = useAppDispatch();

  // Get user data from Redux store
  const { currentUser } = useAppSelector((state: any) => state.auth);

  useEffect(() => {
    HelperMethods.checkUserSession(dispatch);
  }, [dispatch]);

  const loggedInRoutes = (
    <Routes>
      <Route
        path="/"
        element={
          <AccountLayout>
            <MyBookedSkatingSessions />
          </AccountLayout>
        }
      />
      <Route
        path="/tickets"
        element={
          <AccountLayout>
            <Tickets />
          </AccountLayout>
        }
      />
      <Route
        path="/passes"
        element={
          <AccountLayout>
            <Passes />
          </AccountLayout>
        }
      />
      <Route
        path="/parties"
        element={
          <AccountLayout>
            <Parties />
          </AccountLayout>
        }
      />
      <Route
        path="/lessons"
        element={
          <AccountLayout>
            <Lessons />
          </AccountLayout>
        }
      />
      <Route
        path="/hockey"
        element={
          <AccountLayout>
            <Hockey />
          </AccountLayout>
        }
      />
      <Route
        path="/waivers"
        element={
          <AccountLayout>
            <Waivers />
          </AccountLayout>
        }
      />
      <Route
        path="/contact-us"
        element={
          <AccountLayout>
            <ContactUs />
          </AccountLayout>
        }
      />
      <Route
        path="/privacy"
        element={
          <AccountLayout>
            <Privacy />
          </AccountLayout>
        }
      />
      {/* Account Routes */}
      <Route
        path="/my-account"
        element={
          <AccountLayout>
            <MyBookedSkatingSessions />
          </AccountLayout>
        }
      />
      <Route
        path="/my-account/profile"
        element={
          <AccountLayout>
            <Profile />
          </AccountLayout>
        }
      />
      <Route
        path="/my-account/booked-skating-sessions"
        element={
          <AccountLayout>
            <MyBookedSkatingSessions />
          </AccountLayout>
        }
      />
      <Route
        path="/my-account/booked-skating-lessons"
        element={
          <AccountLayout>
            <MyBookedSkatingLessons />
          </AccountLayout>
        }
      />
      <Route
        path="/my-account/booked-hockey-lessons"
        element={
          <AccountLayout>
            <MyBookedHockeyLessons />
          </AccountLayout>
        }
      />
      <Route
        path="/my-account/booked-skating-passes"
        element={
          <AccountLayout>
            <MyBookedSkatingPasses />
          </AccountLayout>
        }
      />
      <Route
        path="/my-account/booked-parties"
        element={
          <AccountLayout>
            <MyBookedParties />
          </AccountLayout>
        }
      />
      <Route
        path="/my-account/submitted-waivers"
        element={
          <AccountLayout>
            <MySubmittedWaivers />
          </AccountLayout>
        }
      />

      {/* Admin Routes - Admin Only */}
      <Route
        path="/admin/emails"
        element={
          <RoleGuard requiredRoles={['admin', 'super_admin']}>
            <AccountLayout>
              <Emails />
            </AccountLayout>
          </RoleGuard>
        }
      />
      <Route
        path="/admin/contact-us-messages"
        element={
          <RoleGuard requiredRoles={['admin', 'super_admin']}>
            <AccountLayout>
              <ContactUsMessages />
            </AccountLayout>
          </RoleGuard>
        }
      />
      <Route
        path="/admin/season-price-list"
        element={
          <RoleGuard requiredRoles={['admin', 'super_admin']}>
            <AccountLayout>
              <SeasonPriceList />
            </AccountLayout>
          </RoleGuard>
        }
      />
      <Route
        path="/admin/skating-sessions"
        element={
          <RoleGuard requiredRoles={['admin', 'super_admin']}>
            <AccountLayout>
              <SkatingSessions />
            </AccountLayout>
          </RoleGuard>
        }
      />
      <Route
        path="/admin/season-settings"
        element={
          <RoleGuard requiredRoles={['admin', 'super_admin']}>
            <AccountLayout>
              <SeasonSettings />
            </AccountLayout>
          </RoleGuard>
        }
      />
      <Route
        path="/admin/skating-passes"
        element={
          <RoleGuard requiredRoles={['admin', 'super_admin']}>
            <AccountLayout>
              <SkatingPasses />
            </AccountLayout>
          </RoleGuard>
        }
      />
      <Route
        path="/admin/waiver"
        element={
          <RoleGuard requiredRoles={['admin', 'super_admin']}>
            <AccountLayout>
              <Waiver />
            </AccountLayout>
          </RoleGuard>
        }
      />
      <Route
        path="/admin/archived-waivers"
        element={
          <RoleGuard requiredRoles={['admin', 'super_admin']}>
            <AccountLayout>
              <ArchivedWaivers />
            </AccountLayout>
          </RoleGuard>
        }
      />
      <Route
        path="/admin/hockey-lessons"
        element={
          <RoleGuard requiredRoles={['admin', 'super_admin']}>
            <AccountLayout>
              <AdminHockeyLessons />
            </AccountLayout>
          </RoleGuard>
        }
      />
      <Route
        path="/admin/skating-lessons"
        element={
          <RoleGuard requiredRoles={['admin', 'super_admin']}>
            <AccountLayout>
              <SkatingLessons />
            </AccountLayout>
          </RoleGuard>
        }
      />
      {/* Moderator Routes - Moderator, Staff, Admin, Super Admin */}
      <Route
        path="/admin/booked-skating-sessions"
        element={
          <RoleGuard
            requiredRoles={['moderator', 'staff', 'admin', 'super_admin']}
          >
            <AccountLayout>
              <BookedSkatingSessions />
            </AccountLayout>
          </RoleGuard>
        }
      />
      <Route
        path="/admin/booked-skating-lessons"
        element={
          <RoleGuard
            requiredRoles={['moderator', 'staff', 'admin', 'super_admin']}
          >
            <AccountLayout>
              <BookedSkatingLessons />
            </AccountLayout>
          </RoleGuard>
        }
      />
      <Route
        path="/admin/booked-hockey-lessons"
        element={
          <RoleGuard
            requiredRoles={['moderator', 'staff', 'admin', 'super_admin']}
          >
            <AccountLayout>
              <BookedHockeyLessons />
            </AccountLayout>
          </RoleGuard>
        }
      />
      <Route
        path="/admin/booked-skating-passes"
        element={
          <RoleGuard
            requiredRoles={['moderator', 'staff', 'admin', 'super_admin']}
          >
            <AccountLayout>
              <BookedSkatingPasses />
            </AccountLayout>
          </RoleGuard>
        }
      />
      <Route
        path="/admin/booked-parties"
        element={
          <RoleGuard
            requiredRoles={['moderator', 'staff', 'admin', 'super_admin']}
          >
            <AccountLayout>
              <BookedParties />
            </AccountLayout>
          </RoleGuard>
        }
      />
      <Route
        path="/admin/customer-waivers"
        element={
          <RoleGuard
            requiredRoles={['moderator', 'staff', 'admin', 'super_admin']}
          >
            <AccountLayout>
              <CustomerWaivers />
            </AccountLayout>
          </RoleGuard>
        }
      />
      <Route
        path="/admin/parties-settings"
        element={
          <RoleGuard requiredRoles={['admin', 'super_admin']}>
            <AccountLayout>
              <PartiesSettings />
            </AccountLayout>
          </RoleGuard>
        }
      />
      <Route
        path="/admin/coupon-management"
        element={
          <RoleGuard requiredRoles={['admin', 'super_admin']}>
            <AccountLayout>
              <CouponManagement />
            </AccountLayout>
          </RoleGuard>
        }
      />
      <Route
        path="/admin/ticket-audit-log"
        element={
          <RoleGuard requiredRoles={['admin', 'super_admin']}>
            <AccountLayout>
              <TicketAuditLog />
            </AccountLayout>
          </RoleGuard>
        }
      />
      <Route
        path="/admin/order-sources"
        element={
          <RoleGuard requiredRoles={['admin', 'super_admin']}>
            <AccountLayout>
              <OrderSources />
            </AccountLayout>
          </RoleGuard>
        }
      />
      <Route
        path="/admin/user-accounts"
        element={
          <RoleGuard
            requiredRoles={['moderator', 'staff', 'admin', 'super_admin']}
          >
            <AccountLayout>
              <UserAccounts />
            </AccountLayout>
          </RoleGuard>
        }
      />
      <Route
        path="/admin/user-accounts/:userId"
        element={
          <RoleGuard
            requiredRoles={['moderator', 'staff', 'admin', 'super_admin']}
          >
            <AccountLayout>
              <UserAccountDetail />
            </AccountLayout>
          </RoleGuard>
        }
      />
      {/* Link-only routes - matches any path like /oct31 (must be after all specific routes) */}
      <Route
        path="/:linkCode"
        element={
          <AccountLayout>
            <Tickets />
          </AccountLayout>
        }
      />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );

  const publicRoutes = (
    <Routes>
      <Route
        path="/"
        element={
          <ContainerLayout>
            <Tickets />
          </ContainerLayout>
        }
      />

      <Route
        path="/sign-in"
        element={
          <PublicRoute>
            <AuthLayout>
              <SignIn />
            </AuthLayout>
          </PublicRoute>
        }
      />

      <Route
        path="/sign-up"
        element={
          <PublicRoute>
            <AuthLayout>
              <SignUp />
            </AuthLayout>
          </PublicRoute>
        }
      />

      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <AuthLayout>
              <ForgotPassword />
            </AuthLayout>
          </PublicRoute>
        }
      />

      <Route
        path="/tickets"
        element={
          <ContainerLayout>
            <Tickets />
          </ContainerLayout>
        }
      />
      <Route
        path="/passes"
        element={
          <ContainerLayout>
            <Passes />
          </ContainerLayout>
        }
      />
      <Route
        path="/parties"
        element={
          <ContainerLayout>
            <Parties />
          </ContainerLayout>
        }
      />
      <Route
        path="/lessons"
        element={
          <ContainerLayout>
            <Lessons />
          </ContainerLayout>
        }
      />
      <Route
        path="/hockey"
        element={
          <ContainerLayout>
            <Hockey />
          </ContainerLayout>
        }
      />
      <Route
        path="/waivers"
        element={
          <ContainerLayout>
            <Waivers />
          </ContainerLayout>
        }
      />
      <Route
        path="/contact-us"
        element={
          <ContainerLayout>
            <ContactUs />
          </ContainerLayout>
        }
      />
      <Route
        path="/privacy"
        element={
          <ContainerLayout>
            <Privacy />
          </ContainerLayout>
        }
      />

      {/* Link-only routes - matches any path like /oct31 */}
      <Route
        path="/:linkCode"
        element={
          <ContainerLayout>
            <Tickets />
          </ContainerLayout>
        }
      />

      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );

  return currentUser ? loggedInRoutes : publicRoutes;
};

export default Main;
